import { userAtom } from '@/atoms/user.atom';
import logo from '@/assets/logo.png';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { execute } from '@/graphql/execute';
import { cn } from '@/lib/utils';
import { saveOnboardingSurveyMutation } from '@/mutations/user.mutation';
import { getOrgSlugFromHostname } from '@/core/domain';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const REFERRAL_OPTIONS = [
  { value: 'search', label: 'Search Engine' },
  { value: 'social', label: 'Social Media' },
  { value: 'colleague', label: 'Colleague / Word of Mouth' },
  { value: 'conference', label: 'Conference / Event' },
  { value: 'other', label: 'Other' },
];

const INDUSTRY_OPTIONS = [
  { value: 'construction', label: 'Construction' },
  { value: 'mining', label: 'Mining' },
  { value: 'surveying', label: 'Surveying' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'government', label: 'Government' },
  { value: 'other', label: 'Other' },
];

const TEAM_SIZE_OPTIONS = [
  { value: '1', label: 'Just me' },
  { value: '2-5', label: '2-5' },
  { value: '6-20', label: '6-20' },
  { value: '21-50', label: '21-50' },
  { value: '50+', label: '50+' },
];

const ROLE_OPTIONS = [
  { value: 'surveyor', label: 'Surveyor' },
  { value: 'engineer', label: 'Engineer' },
  { value: 'project-manager', label: 'Project Manager' },
  { value: 'executive', label: 'Executive' },
  { value: 'other', label: 'Other' },
];

interface RadioGroupProps {
  label: string;
  name: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}

const RadioGroup = ({ label, name, options, value, onChange }: RadioGroupProps) => (
  <div className='grid gap-3'>
    <Label>{label}</Label>
    <div className='flex flex-wrap gap-2'>
      {options.map((option) => (
        <button
          key={option.value}
          type='button'
          onClick={() => onChange(option.value)}
          className={cn(
            'px-4 py-2 rounded-full text-sm border transition-colors',
            value === option.value
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background hover:bg-muted border-border',
          )}>
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

export const OnboardingSurveyForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('organizationId');
  const slug = getOrgSlugFromHostname();
  const [me] = useAtom(userAtom);
  const [isLoading, setIsLoading] = useState(false);

  const [referralSource, setReferralSource] = useState('');
  const [industry, setIndustry] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [role, setRole] = useState('');

  const redirectToOrg = () => {
    // Already on the org's subdomain, just navigate to root
    navigate('/');
  };

  // Skip survey if already completed
  useEffect(() => {
    if (me?.onboardingSurveyCompleted && organizationId) {
      redirectToOrg();
    }
  }, [me?.onboardingSurveyCompleted, organizationId]);

  const handleSubmit = async (skipped: boolean) => {
    setIsLoading(true);

    try {
      await execute(saveOnboardingSurveyMutation, {
        data: skipped
          ? { skipped: true, completedAt: new Date().toISOString() }
          : {
              referralSource,
              industry,
              teamSize,
              role,
              completedAt: new Date().toISOString(),
            },
      });

      if (!skipped) {
        toast.success('Thanks for the info!');
      }

      redirectToOrg();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save survey';
      toast.error('Failed to save survey', { description: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  if (!organizationId) {
    navigate('/auth/onboarding/org');
    return null;
  }

  return (
    <div className={cn('flex flex-col gap-6 w-full max-w-lg mx-auto', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='p-6 md:p-8 bg-white dark:bg-stone-900'>
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col items-center text-center'>
              <img src={logo} alt='Logo' className='size-16 mb-6' />
              <h1 className='text-2xl font-bold'>Tell us about yourself</h1>
              <p className='text-muted-foreground text-balance'>Help us tailor your experience</p>
            </div>

            <RadioGroup
              label='How did you hear about us?'
              name='referralSource'
              options={REFERRAL_OPTIONS}
              value={referralSource}
              onChange={setReferralSource}
            />

            <RadioGroup
              label='What industry are you in?'
              name='industry'
              options={INDUSTRY_OPTIONS}
              value={industry}
              onChange={setIndustry}
            />

            <RadioGroup
              label='What is your team size?'
              name='teamSize'
              options={TEAM_SIZE_OPTIONS}
              value={teamSize}
              onChange={setTeamSize}
            />

            <RadioGroup
              label='What is your primary role?'
              name='role'
              options={ROLE_OPTIONS}
              value={role}
              onChange={setRole}
            />

            <div className='flex gap-3'>
              <Button
                type='button'
                variant='outline'
                className='flex-1'
                onClick={() => handleSubmit(true)}
                disabled={isLoading}>
                Skip
              </Button>
              <Button
                type='button'
                className='flex-1'
                onClick={() => handleSubmit(false)}
                disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Continue'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
